import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchWhisperCircle,
  optInWhisper,
  submitWhisperReflection,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useEntitlements } from "@/lib/entitlements";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import UserBadge from "@/components/UserBadge";
import SpeakButton from "@/components/SpeakButton";
import { getReflectionVoice } from "@/lib/reflectionVoice";
import { ChevronLeft, Loader2, Users, Check, Feather } from "lucide-react";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export default function WhisperCircle() {
  const { user } = useAuth();
  const { entitlements } = useEntitlements();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await fetchWhisperCircle();
      setState(r);
    } catch (e) {
      console.error("whisper circle fetch failed", e);
    }
    finally {
      setLoading(false);
    }
    // refresh closes over nothing beyond state setters (stable identities).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 20s to see if partner has submitted
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const optIn = async () => {
    setBusy(true);
    try {
      await optInWhisper(true);
      toast.success("You have joined the Whisper Circle.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join the circle.");
    } finally {
      setBusy(false);
    }
  };

  const optOut = async () => {
    setBusy(true);
    try {
      await optInWhisper(false);
      toast.success("You have stepped out of the circle.");
      await refresh();
    } catch (e) {
      toast.error("Could not update your preference.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (draft.trim().length < 8) {
      toast.error("Please write a little more, gently.");
      return;
    }
    setBusy(true);
    try {
      await submitWhisperReflection(draft.trim());
      setDraft("");
      toast.success("Your whisper has been received.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your reflection.");
    } finally {
      setBusy(false);
    }
  };

  const isSub = entitlements?.subscription_active;

  return (
    <div className="min-h-screen relative">
      <div className="max-w-3xl mx-auto px-6 md:px-16 pt-14 md:pt-20 pb-32 relative z-10">
        <div className="flex items-center justify-between mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 min-h-[44px] -mx-2 px-2 sm:min-h-0 sm:mx-0 sm:px-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
            data-testid={TID.backToDashboard}
          >
            <ChevronLeft className="h-3 w-3" /> The Workbook
          </Link>
          <UserBadge />
        </div>

        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-4">
          The Whisper Circle
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-bone-100 mb-6 leading-tight" data-testid="whisper-title">
          Two strangers.<br />
          <em className="text-bone-300 italic">One prompt.</em> One quiet exchange.
        </h1>
        <p className="font-sans text-base text-bone-300 leading-relaxed mb-12 max-w-xl">
          Each week you are paired anonymously with another Umbra Premium member. You both answer
          the same prompt in private, and Claude gently rewrites each answer as a whisper for the
          other. Only for those who opt in.
        </p>

        {loading && <p className="font-sans text-bone-500">Listening…</p>}

        {!loading && !isSub && (
          <div className="border border-obsidian-600 p-8 md:p-10">
            <p className="font-serif text-2xl text-bone-100 mb-4">A private circle.</p>
            <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
              The Whisper Circle is offered to Umbra Premium members so the space stays small,
              intentional, and safe. Begin the subscription to join.
            </p>
            <Link
              to="/dashboard"
              className="inline-block font-sans text-xs uppercase tracking-[0.24em] text-terracotta border-b border-terracotta pb-1 hover:text-terracotta-soft transition-colors"
            >
              Return to the workbook
            </Link>
          </div>
        )}

        {!loading && isSub && !state?.opted_in && (
          <div className="border border-obsidian-600 p-8 md:p-10">
            <div className="flex items-start gap-4 mb-6">
              <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <p className="font-serif text-2xl text-bone-100 mb-2">Would you like to be paired this week?</p>
                <p className="font-sans text-sm text-bone-400 leading-relaxed">
                  You can step in or out at any time. Nothing about you is shared — not your name,
                  not your email, not even your city. Only the shape of your whisper.
                </p>
              </div>
            </div>
            <Button
              onClick={optIn}
              disabled={busy}
              data-testid="whisper-optin-btn"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5 transition-colors"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
              Join the circle
            </Button>
          </div>
        )}

        {!loading && isSub && state?.opted_in && (
          <div className="space-y-8">
            <div className="border-l-2 border-terracotta pl-6 py-2 flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-3">
                  This week's prompt
                </p>
                <p className="font-serif text-2xl md:text-3xl text-bone-100 leading-snug italic">
                  {state.prompt}
                </p>
              </div>
              <SpeakButton
                text={state.prompt}
                size="sm"
                testId="speak-whisper-prompt"
                label="Read this week's prompt aloud"
              />
            </div>

            {!state.pairing && (
              <div className="border border-obsidian-600 p-6">
                <p className="font-serif text-lg text-bone-100 mb-2">Awaiting a partner…</p>
                <p className="font-sans text-sm text-bone-400 leading-relaxed">
                  You have joined the circle for this week. As soon as another journaler opts in,
                  you will be paired and this page will update.
                </p>
              </div>
            )}

            {state.pairing && !state.pairing.you_submitted && (
              <div>
                <label className="block font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-500 mb-3">
                  Your whisper
                </label>
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Something honest. Something small. It doesn't have to be beautiful."
                  rows={6}
                  data-testid="whisper-draft"
                  className="bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-serif text-lg leading-relaxed rounded-none placeholder:text-bone-500 mb-4"
                />
                <Button
                  onClick={submit}
                  disabled={busy || !draft.trim()}
                  data-testid="whisper-submit-btn"
                  className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5 transition-colors"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Feather className="h-4 w-4 mr-2" />}
                  Send this whisper
                </Button>
              </div>
            )}

            {state.pairing?.you_submitted && !state.pairing?.exchange_ready && (
              <div className="border border-obsidian-600 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Check className="h-4 w-4 text-terracotta" />
                  <p className="font-sans uppercase tracking-[0.24em] text-xs text-terracotta">
                    Received
                  </p>
                </div>
                <p className="font-serif text-lg text-bone-100 mb-2">Your whisper is being held.</p>
                <p className="font-sans text-sm text-bone-400 leading-relaxed">
                  As soon as your partner also submits theirs, Claude will gently rewrite each for
                  the other and deliver both here. This page updates on its own.
                </p>
              </div>
            )}

            {state.pairing?.exchange_ready && (
              <div className="animate-fade-up">
                <div className="border-l-2 border-terracotta pl-6 py-2 mb-6 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-3">
                      A whisper for you
                    </p>
                    <p className="font-serif text-lg md:text-xl text-bone-100 leading-relaxed italic">
                      {state.pairing.partner_reflection_mediated}
                    </p>
                  </div>
                  <SpeakButton
                    text={state.pairing.partner_reflection_mediated}
                    voice={getReflectionVoice(user?.preferred_voice)}
                    size="sm"
                    testId="speak-whisper-mediated"
                    label="Hear your partner's whisper"
                  />
                </div>
                <p className="font-sans text-sm text-bone-400 leading-relaxed">
                  Your own whisper has been softened and delivered, too. Next week a new prompt and
                  a new partner. You may stay in the circle or step out at any time.
                </p>
              </div>
            )}

            <div className="pt-8 border-t border-obsidian-600">
              <button
                onClick={optOut}
                disabled={busy}
                data-testid="whisper-optout-btn"
                className="font-sans text-xs uppercase tracking-[0.24em] text-bone-500 hover:text-terracotta transition-colors"
              >
                Step out of the circle
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
